import { useEffect, useState } from 'react'
import { Box, Tab, Tabs } from '@mui/material'
import { Link, Outlet, Route, Routes, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useBasePath, useMember } from 'components/Providers'
import { useSelector } from 'react-redux'
import Contacts from '../Contact'
import Documents from '../Document'
import FinalGrades from '../FinalGrades'
import { STUDENT } from 'constants'
import MemberProfile from './Profile'
import StateData from '../StateData'
import { Schedule } from '../Schedule'
import Enrollments from '../Enrollments'
import Attendance from '../Attendance'
// import Roles from '../Roles'
import 'utils/global.scss'
import { hasPermission } from 'store'
import NoAccess from 'components/NoAccess'
import RedirectToFirstPermitted from 'components/RedirectToPermittedTab'
import ProfileDisciplineStats from '../DisciplineStats'

export default function TabControl() {
  const { memberId, '*': tabName } = useParams()
  const theme = useSelector(state => state.session.appearance)
  const member = useMember()
  const { district } = useSelector(state => state.session)
  const employmentType = member?.person?.type
  const academicYearEnrollment = member?.academicYearEnrollment
  const state = useSelector(state => state.session?.campus?.address?.state || '')
  const basePath = useBasePath()
  const { t } = useTranslation()

  const tabConfig = [
    {
      index: 0,
      label: "Profile",
      path: "profile",
      permission: `${employmentType}.profile`,
    },
    {
      index: 1,
      label: "Contacts",
      path: "contacts",
      permission: `${employmentType}.contact`,
      disabled: String(memberId) === "new",
    },
    {
      index: 2,
      label: "Documents",
      path: "documents",
      permission: `${employmentType}.document`,
      visible: employmentType === STUDENT,
      disabled: String(memberId) === "new",
    },
    {
      index: 3,
      label: "Enrollments",
      path: "enrollments",
      permission: `${employmentType}.enrollment`,
      visible: employmentType === STUDENT,
      disabled: String(memberId) === "new",
    },
    {
      index: 4,
      label: "Attendance",
      path: "attendance",
      permission: `${employmentType}.statistics`,
      visible: employmentType === STUDENT && academicYearEnrollment,
      disabled: String(memberId) === "new",
    },
    {
      index: 5,
      label: "Schedule",
      path: "schedule",
      permission: employmentType === STUDENT ? `${employmentType}.statistics` : `${employmentType}.schedule`,
      visible: !district?.isServiceProvider,
      disabled: String(memberId) === "new",
    },
    {
      index: 6,
      label: "Final Grades",
      path: "final-grades",
      permission: `${employmentType}.statistics`,
      visible: employmentType === STUDENT && academicYearEnrollment,
      disabled: String(memberId) === "new",
    },
    {
      index: 7,
      label: "Discipline Stats",
      path: "discipline-stats",
      permission: `${employmentType}.statistics`,
      visible: employmentType === STUDENT && academicYearEnrollment,
      disabled: String(memberId) === "new",
    },
    {
      index: 8,
      label: "State Data",
      path: "state-data",
      permission: `${employmentType}.state`,
      visible:
        employmentType === STUDENT &&
        academicYearEnrollment &&
        state === "NJ" &&
        !district?.isServiceProvider,
      disabled: String(memberId) === "new",
    },
  ]

  const permittedTabs = tabConfig.filter(
    tab =>
      hasPermission(tab.permission) &&
      (tab.visible !== false) 
  )

  useEffect(() => {
    setTabIndex(getTabIndex(tabName))
  }, [tabName])

  const getTabIndex = (path) => {
    const name = path?.split("/")?.[0]
    const found = permittedTabs.find(tab => tab.path === name)
    return found ? found.index : permittedTabs[0]?.index ?? 0
  }

  const [tabIndex, setTabIndex] = useState(getTabIndex(tabName))

  useEffect(() => {
    setTabIndex(getTabIndex(tabName))
  }, [tabName, permittedTabs.length])

  const renderComponent = (path) => {
    switch (path) {
      case "profile": return <MemberProfile />
      case "person": return <MemberProfile />
      case "contacts": return <Contacts />
      case "documents": return <Documents />
      case "enrollments": return <Enrollments />
      case "attendance": return <Attendance />
      case "schedule": return <Schedule />
      case "final-grades": return <FinalGrades />
      case "discipline-stats": return <ProfileDisciplineStats />
      case "state-data": return <StateData />
      default: return <NoAccess />
    }
  }

  return (
    <Box>
      {/* Tabs */}
      {memberId && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', displayPrint: 'none' }}>
          <Tabs value={tabIndex} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile >
            {permittedTabs.map(tab => (
              <Tab
                key={tab.path}
                label={t(tab.label)}
                value={tab.index}
                disabled={tab.disabled}
                component={Link}
                to={`${basePath}/${tab.path}`}
                className={
                  tabIndex === tab.index
                    ? `tab-item-selected`
                    : `tab-item-${theme}`
                }
              />
            ))}
          </Tabs>
        </Box>
      )}

      <Routes>
        <Route path="" element={<Outlet />}>
          <Route
            index
            element={
              permittedTabs.length ? (
                <RedirectToFirstPermitted
                  basePath={basePath}
                  items={tabConfig}
                />
              ) : (
                <NoAccess />
              )
            }
          />

          {permittedTabs.map(tab => (
            <Route
              key={tab.path}
              path={`${tab.path}/*`}
              element={renderComponent(tab.path)}
            />
          ))}

          <Route path="person/*" element={renderComponent('person')} />

          <Route
            path="*"
            element={
              permittedTabs.length ? (
                <RedirectToFirstPermitted basePath={basePath} items={tabConfig} />
              ) : (
                <NoAccess />
              )
            }
          />
        </Route>
      </Routes>
    </Box>
  )
}
